import { useState } from 'react'
import { Trans } from '@lingui/react/macro'
import { useTranslations } from '@/lib/next-shim'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Grid'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Button from '@mui/material/Button'
import Avatar from '@mui/material/Avatar'
import MuiLink from '@mui/material/Link'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import EmailIcon from '@mui/icons-material/Email'

const faqIds = [1, 2, 3, 4, 5, 6] as const

const accordionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '12px !important',
  '&:before': { display: 'none' },
  boxShadow: 'none',
}

function FaqAccordions() {
  const [expanded, setExpanded] = useState<number | false>(false)
  const tFaq = useTranslations('marketing.sections.FAQs')

  const handleChange = (panel: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {faqIds.map((id) => (
        <Accordion key={id} expanded={expanded === id} onChange={handleChange(id)} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>
              {tFaq(`faqs.${id}.question`)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary">
              {tFaq(`faqs.${id}.answer`)}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  )
}

function ContactSidebar() {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3 },
        borderRadius: 4,
        bgcolor: 'primary.50',
        border: '1px solid',
        borderColor: 'primary.100',
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        <Trans>Contact & Support</Trans>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        <Trans>Can't find the answer to your question? Our support team is here to help you.</Trans>
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'background.paper', boxShadow: 1 }}>
          <EmailIcon color="primary" sx={{ fontSize: 16 }} />
        </Avatar>
        <Box>
          <Typography variant="overline" color="text.disabled" sx={{ fontSize: 9, lineHeight: 1.2 }}>
            Email
          </Typography>
          <MuiLink href="mailto:support.medconnect@promotal.com" variant="caption" fontWeight={500} display="block" sx={{ lineHeight: 1.3 }}>
            support.medconnect@promotal.com
          </MuiLink>
        </Box>
      </Box>
      <Button
        variant="contained"
        fullWidth
        href="mailto:support.medconnect@promotal.com"
        size="small"
        sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, py: 1 }}
      >
        <Trans>Contact us</Trans>
      </Button>
    </Paper>
  )
}

export function FaqAndContactSection() {
  return (
    <Grid container spacing={3} alignItems="flex-start">
      <Grid item xs={12} lg={8}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
          <Trans>Frequently asked questions</Trans>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <Trans>Quick answers to your most common questions.</Trans>
        </Typography>
        <FaqAccordions />
      </Grid>
      <Grid item xs={12} lg={4}>
        <ContactSidebar />
      </Grid>
    </Grid>
  )
}
